import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';

import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import type {
  ReportInsightsUpdateDTO,
  ReportsInsightsDTO
} from './SocMonthlySummaryDialog.types';

export const useFetchReportInsights = () => {
  const handleError = useHandleError();
  const { data, isError, error, isFetching } = useQuery<ReportsInsightsDTO[]>({
    queryKey: ['report_insights/']
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch announcements.');
    }
  }, [isError, data]);

  return {
    data,
    isFetching,
    error
  };
};

export const useSaveReportInsights = () => {
  const handleError = useHandleError();

  return useMutation({
    mutationFn: ({
      reportInsights,
      id
    }: {
      reportInsights: ReportInsightsUpdateDTO;
      id: number | null;
    }) => {
      if (id) {
        // If id is provided, update the existing report insights
        return axiosClient.put<ReportInsightsUpdateDTO>(
          `/report_insights/${id}`,
          reportInsights
        );
      }
      // If id is not provided, create new report insights
      return axiosClient.post<ReportInsightsUpdateDTO>(
        '/report_insights/',
        reportInsights
      );
    },
    onSuccess: () => {
      enqueueSnackbar(`SOC monthly summary saved successfully.`, {
        variant: 'success'
      });
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failed to save or update SOC monthly summary.');
    }
  });
};
